import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Observable, Subscription } from 'rxjs';
import { Graph } from '../../models/graph';
import { ChartModule, UIChart } from '../../primeng/chart/chart.component';
import { Constants } from '../../utilities/constants';

@Component({
  selector: 'app-ecg',
  imports: [
    FormsModule,
    PanelModule,
    ProgressSpinnerModule,
    ChartModule,
    ButtonModule,
    SelectButtonModule,
    TranslatePipe
  ],
  templateUrl: './ecg.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ecg.component.css'
})
export class EcgComponent  {
  private _segnale: Graph[][] = [];

  private _xMin: number = 0;
  private _xMax: number = 0;


  @Input() showInfo: boolean = false;

  @Input() set segnale(value: Graph[][]) {
    this._segnale = value;

    this.data.datasets = [];
    this.initialZoom = [];
    this._segnale.forEach(v => {
      if (v.length > 0) {
        this.data.datasets.push({
          data: v,
          borderColor: '#1a1a1a', // Nero intenso per simulare l'inchiostro o la punta termica
          borderWidth: 1,         // Linea sottile per evitare l'effetto blocco
          pointRadius: 0,         /* Nasconde i pallini per migliorare le performance */
          fill: false,
          parsing: false,
        })
      }
    })
  }
  get segnale(): Graph[][] {
    return this._segnale;
  }

  @Input() set xMax(value: number) {
    this.options.scales.x.max = value;
    this._xMax = value;
  }
  get xMax(): number {
    return this._xMax;
  }

  @Input() set xMin(value: number) {
    this.options.scales.x.min = value;
    this._xMin = value;
  }
  get xMin(): number {
    return this._xMin;
  }

  @Input() loading: boolean = false;

  @ViewChild('chart') myChartRef: UIChart | undefined;

  data: any;
  options: any;
  plugins: any[] = [];

  initialZoom: {id: string, value: number}[] = [];

  modeOptions:  any[] = [
    { icon: 'pi pi-search', value: 1 },
    { icon: 'pi pi-arrows-alt', value: 2 }
  ];
  mode: number = 2;


  constructor() {
    this.plugins = [zoomPlugin];

    this.data = {
      datasets: []
    }

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false, // Disattiva le animazioni per gestire grandi moli di dati
      indexAxis: 'x', // 1. FONDAMENTALE per la decimazione con parsing:false
 //       spanGaps: true,    // Evita buchi nel grafico se mancano millisecondi
      scales: {
        x: {
          type: 'time',
          time: {
            unit: 'second',
            tooltipFormat: 'HH:mm:ss.SSS', // Mostra anche i millisecondi nel tooltip
            displayFormats: {
              minute: 'HH:mm:ss',
              second: 'HH:mm:ss'
            }
          },
          ticks: {
            color: '#333',
            source: 'auto',
            maxTicksLimit: 10 // Limita i testi sull'asse X per non sovrapporli
          },
          grid: {
            color: 'rgba(240, 100, 100, 0.5)', // Rosso/Arancio marcato
            lineWidth: 1
          }
        },
        y: {
          type: 'linear',
          min: -3.5,
          max: 3.5,
          title: {
            display: true,
            text: 'mV'
          },
          ticks: {
            stepSize: 0.5
          },
          grid: {
            color: 'rgba(240, 100, 100, 0.5)', // Rosso/Arancio marcato
            lineWidth: 1
          },
          // 2. Configurazione delle Linee Secondarie (Ogni 0.1)
          minor: {
            ticks: {
              stepSize: 0.1 // Specifica il passo per i sotto-intervalli
            }
          }
        }
      },
      plugins: {
        // 1. Attiva la decimazione dei dati
        decimation: {
          enabled: true,
          algorithm: 'lttb', // Preserva i picchi (ottimo per ECG)
          samples: 10000,     // Numero di punti massimi da mostrare nell'area visibile
        },
        legend: {
          display: false
        },
        zoom: {
          pan: {
            enabled: true,
            mode: 'xy',
          },
          zoom: {
            wheel: {
              enabled: false,
            },
            pinch: {
              enabled: false,
            },
            drag: {
              enabled: false,
              borderWidth: 4
            },
            mode: 'xy',
            scaleMode: 'xy',

            // Intercettiamo lo zoom PRIMA che venga completato per alterare le proporzioni
            onZoom: (mychart: any) => {
              const scaleX = mychart.scales.x;
              const scaleY = mychart.scales.y;

              // 1. Calcoliamo la variazione attuale dell'asse X
              const rangeX = scaleX.max - scaleX.min;
      
              // 2. Modifichiamo manualmente i limiti di X per "esasperare" lo zoom (Stretch Maggiore)
              // Modificando il fattore (es. 0.05) puoi aumentare o diminuire l'intensità dello stretch
              const stretchFactor = 0.05; 
              const deltaX = rangeX * stretchFactor;

              // Applichiamo lo stretch stringendo ulteriormente i limiti di X verso il centro
              scaleX.options.min = scaleX.min + deltaX;
              scaleX.options.max = scaleX.max - deltaX;

              // 3. (Opzionale) Freniamo l'asse Y se noti che si stringe troppo velocemente
              // Puoi commentare queste righe se l'asse Y ti va bene così com'è
              if (scaleY.min < -3.5) scaleY.options.min = -3.5;
              if (scaleY.max > 3.5) scaleY.options.max = 3.5;
            }
          }
        }
      }
    };
  }

  onModeSelection(): void {
    const useZoom: boolean = this.mode === 1;
    const usePan: boolean = this.mode === 2;

    this.options.plugins.zoom.pan.enabled = usePan;
    this.options.plugins.zoom.zoom.wheel.enabled = false;
    this.options.plugins.zoom.zoom.pinch.enabled = false;
    this.options.plugins.zoom.zoom.drag.enabled = useZoom;

    this.myChartRef?.chart.update();
  }

   onZoomIn() {
    this.myChartRef?.chart.zoom(1.2);
  }

  onZoomOut() {
    this.myChartRef?.chart.zoom(0.8);
  }

  onZoomReset() {
    this.myChartRef?.chart.resetZoom();
  }
}
